import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from 'chart.js';
import { FaCalendarAlt, FaUsers } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import './ClubProfile.css';

// npm i xlsx-js-style
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

// helper truthy robust (0/1, true/false, '1'/'0')
const toBool = (v) => v === true || v === 1 || v === '1' || v === 'true';

const resolveActive = (x) => {
  const candidates = [
    x.is_active,
    x.active,
    x.status_active,
    x.is_valid,
    x.valid,
    x.is_legitimated,
    x.legitimated,
  ];

  const v = candidates.find((v) => v !== undefined && v !== null && v !== '');
  if (v === undefined) return { active: null, label: 'Necunoscut' };

  const s = String(v).toLowerCase().trim();
  const truthy = ['1', 'true', 'da', 'yes', 'active', 'activ'];
  const falsy = ['0', 'false', 'nu', 'no', 'inactive', 'inactiv'];

  if (truthy.includes(s)) return { active: true, label: 'Activ' };
  if (falsy.includes(s)) return { active: false, label: 'Inactiv' };

  const asNum = Number(v);
  if (!Number.isNaN(asNum)) {
    return asNum > 0 ? { active: true, label: 'Activ' } : { active: false, label: 'Inactiv' };
  }

  return { active: null, label: 'Necunoscut' };
};

const safeFileName = (name) =>
  (name || 'export')
    .toString()
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '_');

export default function AdminProfile() {
  const navigate = useNavigate();

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  const [athletes, setAthletes] = useState([]);
  const [referees, setReferees] = useState([]);
  const [coaches, setCoaches] = useState([]);

  const token = localStorage.getItem('adminToken');

  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = 2025; y <= currentYear + 1; y++) years.push(y);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const cacheBuster = Date.now();

        // ✅ IMPORTANT:
        // - sportivi: show-athletes.php (are cnp + toți)
        // - arbitri: show-referees.php (toți + club_name + categorie)
        // - antrenori: show-coaches.php (toți + club_name)
        const [aRes, rRes, cRes] = await Promise.all([
          fetch(`https://www.fro.ro/api/athletes/show-athletes.php?year=${selectedYear}&_=${cacheBuster}`, {
            headers: { Authorization: 'Bearer ' + token }
          }),
          fetch(`https://www.fro.ro/api/referees/show-referees.php?year=${selectedYear}&_=${cacheBuster}`, {
            headers: { Authorization: 'Bearer ' + token }
          }),
          fetch(`https://www.fro.ro/api/coaches/show-coaches.php?year=${selectedYear}&_=${cacheBuster}`, {
            headers: { Authorization: 'Bearer ' + token }
          }),
        ]);

        const aj = await aRes.json();
        const rj = await rRes.json();
        const cj = await cRes.json();

        setAthletes(aj?.athletes || []);
        setReferees(rj?.referees || []);
        setCoaches(cj?.coaches || []);
      } catch (e) {
        console.error(e);
        setAthletes([]);
        setReferees([]);
        setCoaches([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [selectedYear, token, navigate]);

  // ====== STATS ======
  const stats = useMemo(() => {
    const athletesTotal = athletes.length;
    const athletesActive = athletes.filter((x) => toBool(x.is_legitimated)).length;

    const refereesTotal = referees.length;
    const refereesActive = referees.filter((x) => toBool(x.is_legitimated)).length;

    const coachesTotal = coaches.length;
    const coachesActive = coaches.filter((x) => toBool(x.is_legitimated)).length;

    return {
      athletes: { total: athletesTotal, active: athletesActive, inactive: athletesTotal - athletesActive },
      referees: { total: refereesTotal, active: refereesActive, inactive: refereesTotal - refereesActive },
      coaches: { total: coachesTotal, active: coachesActive, inactive: coachesTotal - coachesActive },
    };
  }, [athletes, referees, coaches]);

  // ====== EXPORT ======
  const exportAllMembers = async (yearToExport = selectedYear) => {
    try {
      if (!token) {
        alert('Nu există token admin.');
        return;
      }

      const cacheBuster = Date.now();

      const [aRes, rRes, cRes] = await Promise.all([
        fetch(`https://www.fro.ro/api/athletes/show-athletes.php?year=${yearToExport}&_=${cacheBuster}`, {
          headers: { Authorization: 'Bearer ' + token }
        }),
        fetch(`https://www.fro.ro/api/referees/show-referees.php?year=${yearToExport}&_=${cacheBuster}`, {
          headers: { Authorization: 'Bearer ' + token }
        }),
        fetch(`https://www.fro.ro/api/coaches/show-coaches.php?year=${yearToExport}&_=${cacheBuster}`, {
          headers: { Authorization: 'Bearer ' + token }
        }),
      ]);

      if (!aRes.ok || !rRes.ok || !cRes.ok) {
        throw new Error('Eroare la descărcarea listelor pentru export.');
      }

      const aj = await aRes.json();
      const rj = await rRes.json();
      const cj = await cRes.json();

      const athletesRaw = aj?.athletes || [];
      const refereesRaw = rj?.referees || [];
      const coachesRaw = cj?.coaches || [];

      // ✅ scoate „TEST_...” din export (doar rândurile de demo)
      const isTestRow = (x) => {
        const ln = String(x?.last_name ?? '').toUpperCase();
        const fn = String(x?.first_name ?? '').toUpperCase();
        const email = String(x?.email ?? '').toUpperCase();
        const cnp = String(x?.cnp ?? '').toUpperCase();
        return (
          ln.startsWith('TEST') ||
          fn.startsWith('TEST') ||
          email.includes('TEST@') ||
          cnp.startsWith('TEST')
        );
      };

      const athletesFiltered = athletesRaw.filter((x) => !isTestRow(x));
      const refereesFiltered = refereesRaw.filter((x) => !isTestRow(x));
      const coachesFiltered = coachesRaw.filter((x) => !isTestRow(x));

      // ========== HEADERS ==========
      const athleteHeaders = [
        'Nume',
        'Prenume',
        'An naștere',
        'CNP',
        'Email',
        'Telefon',
        'Număr legitimație',
        'Club',
        'Status activitate',
        'Activ',
      ];

      const commonHeaders = [
        'Nume',
        'Prenume',
        'An naștere',
        'Email',
        'Telefon',
        'Număr legitimație',
        'Club',
        'Status activitate',
        'Activ',
      ];

      const refereeHeaders = [...commonHeaders, 'Tip / Categorie'];

      // ====== STILURI EXCEL ======
      const HEADER_BG = '244062'; // #244062 fără #
      const WHITE = 'FFFFFF';

      const headerStyle = {
        font: { name: 'Poppins', color: { rgb: WHITE }, bold: true },
        fill: { patternType: 'solid', fgColor: { rgb: HEADER_BG } },
        alignment: { vertical: 'center', horizontal: 'center', wrapText: true },
      };

      const firstColStyle = {
        font: { name: 'Poppins', color: { rgb: WHITE }, bold: true },
        fill: { patternType: 'solid', fgColor: { rgb: HEADER_BG } },
        alignment: { vertical: 'center', horizontal: 'center' },
      };

      const bodyStyle = {
        font: { name: 'Poppins' },
        alignment: { vertical: 'center' },
      };

      const applySheetStyling = (ws, totalRows, totalCols) => {
        const cellAddr = (r, c) => XLSX.utils.encode_cell({ r, c });

        for (let r = 0; r < totalRows; r++) {
          for (let c = 0; c < totalCols; c++) {
            const addr = cellAddr(r, c);
            if (!ws[addr]) continue;

            // linia header (include NR CRT) -> albastru + alb
            if (r === 0) {
              ws[addr].s = headerStyle;
              continue;
            }

            // prima coloană (NR CRT) -> albastru + alb
            if (c === 0) {
              ws[addr].s = firstColStyle;
              continue;
            }

            // restul -> poppins
            ws[addr].s = bodyStyle;
          }
        }
      };

      // ====== AOA builder cu NR CRT ======
      const makeAOA = (rows, headers, mapper) => {
        const aoa = [['NR CRT', ...headers]];
        rows.forEach((x, idx) => {
          const { active, label } = resolveActive(x);
          const mapped = mapper(x, { active, label });
          aoa.push([idx + 1, ...mapped]);
        });
        return aoa;
      };

      const wb = XLSX.utils.book_new();

      // ================== SPORTIVI ==================
      const athletesAOA = makeAOA(athletesFiltered, athleteHeaders, (x, { active, label }) => ([
        x?.last_name ?? '',
        x?.first_name ?? '',
        x?.birth_year ?? '',
        String(x?.cnp ?? ''),
        x?.email ?? '',
        x?.phone ?? '',
        x?.nr_legitimatie ?? '',
        x?.club_name ?? '',
        label,
        active === null ? '' : active ? 1 : 0,
      ]));

      const wsAthletes = XLSX.utils.aoa_to_sheet(athletesAOA);
      wsAthletes['!cols'] = [
        { wch: 8 },   // NR CRT
        { wch: 16 },  // Nume
        { wch: 16 },  // Prenume
        { wch: 10 },  // An naștere
        { wch: 18 },  // CNP
        { wch: 26 },  // Email
        { wch: 16 },  // Telefon
        { wch: 16 },  // Număr legitimație
        { wch: 30 },  // Club
        { wch: 14 },  // Status activitate
        { wch: 8 },   // Activ
      ];
      applySheetStyling(wsAthletes, athletesAOA.length, athletesAOA[0].length);
      XLSX.utils.book_append_sheet(wb, wsAthletes, 'Sportivi');

      // ================== ARBITRI ==================
      const refereesAOA = makeAOA(refereesFiltered, refereeHeaders, (x, { active, label }) => ([
        x?.last_name ?? '',
        x?.first_name ?? '',
        x?.birth_year ?? '',
        x?.email ?? '',
        x?.phone ?? '',
        x?.license_number ?? '',
        x?.club_name ?? '',
        label,
        active === null ? '' : active ? 1 : 0,
        x?.categorie ?? '-',
      ]));

      const wsReferees = XLSX.utils.aoa_to_sheet(refereesAOA);
      wsReferees['!cols'] = [
        { wch: 8 },   // NR CRT
        { wch: 16 },  // Nume
        { wch: 16 },  // Prenume
        { wch: 10 },  // An naștere
        { wch: 26 },  // Email
        { wch: 16 },  // Telefon
        { wch: 16 },  // Număr legitimație
        { wch: 30 },  // Club
        { wch: 14 },  // Status activitate
        { wch: 8 },   // Activ
        { wch: 16 },  // Tip / Categorie
      ];
      applySheetStyling(wsReferees, refereesAOA.length, refereesAOA[0].length);
      XLSX.utils.book_append_sheet(wb, wsReferees, 'Arbitri');

      // ================== ANTRENORI ==================
      const coachesAOA = makeAOA(coachesFiltered, commonHeaders, (x, { active, label }) => ([
        x?.last_name ?? '',
        x?.first_name ?? '',
        x?.birth_year ?? '',
        x?.email ?? '',
        x?.phone ?? '',
        x?.nr_legitimatie ?? x?.license_number ?? '',
        x?.club_name ?? '',
        label,
        active === null ? '' : active ? 1 : 0,
      ]));

      const wsCoaches = XLSX.utils.aoa_to_sheet(coachesAOA);
      wsCoaches['!cols'] = [
        { wch: 8 },   // NR CRT
        { wch: 16 },  // Nume
        { wch: 16 },  // Prenume
        { wch: 10 },  // An naștere
        { wch: 26 },  // Email
        { wch: 16 },  // Telefon
        { wch: 16 },  // Număr legitimație
        { wch: 30 },  // Club
        { wch: 14 },  // Status activitate
        { wch: 8 },   // Activ
      ];
      applySheetStyling(wsCoaches, coachesAOA.length, coachesAOA[0].length);
      XLSX.utils.book_append_sheet(wb, wsCoaches, 'Antrenori');

      const fileName = `Export_ADMIN_membri_${safeFileName(yearToExport)}_${Date.now()}.xlsx`;

      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(
        new Blob([excelBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        fileName
      );
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Eroare la export.');
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Se încarcă datele...</p>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {/* SELECTOR AN */}
      <div className="year-selector">
        <FaCalendarAlt className="calendar-icon" />
        <label>An:</label>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="year-dropdown"
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y === currentYear ? `${y} (curent)` : `${y} `}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="export-btn"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); exportAllMembers(selectedYear); }}
        >
          Export membri (toate cluburile) — {selectedYear}
        </button>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={selectedYear}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          <div className="cards-grid">
            <div className="cardp">
              <FaUsers className="card-icon purple" />
              <h3 className="card-title">Sportivi — {selectedYear}</h3>
              <p className="card-value">{stats.athletes.total}</p>
              <small>Activi: {stats.athletes.active}</small><br />
              <small>Inactivi: {stats.athletes.inactive}</small>
            </div>

            <div className="cardp">
              <FaUsers className="card-icon purple" />
              <h3 className="card-title">Arbitri — {selectedYear}</h3>
              <p className="card-value">{stats.referees.total}</p>
              <small>Activi: {stats.referees.active}</small><br />
              <small>Inactivi: {stats.referees.inactive}</small>
            </div>

            <div className="cardp">
              <FaUsers className="card-icon purple" />
              <h3 className="card-title">Antrenori — {selectedYear}</h3>
              <p className="card-value">{stats.coaches.total}</p>
              <small>Activi: {stats.coaches.active}</small><br />
              <small>Inactivi: {stats.coaches.inactive}</small>
            </div>
          </div>

          {/* GRAFICE */}
          <div className="charts-grid">
            <div className="chart-card">
              <h4>Sportivi: Activ vs Inactiv</h4>
              <Pie
                data={{
                  labels: ['Activ', 'Inactiv'],
                  datasets: [{
                    data: [stats.athletes.active, stats.athletes.inactive],
                    backgroundColor: ['#845ef7', '#e9ecef'],
                    borderWidth: 1
                  }],
                }}
                options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }}
              />
            </div>

            <div className="chart-card">
              <h4>Arbitri: Activ vs Inactiv</h4>
              <Pie
                data={{
                  labels: ['Activ', 'Inactiv'],
                  datasets: [{
                    data: [stats.referees.active, stats.referees.inactive],
                    backgroundColor: ['#51cf66', '#e9ecef'],
                    borderWidth: 1
                  }],
                }}
                options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }}
              />
            </div>

            <div className="chart-card">
              <h4>Antrenori: Activ vs Inactiv</h4>
              <Pie
                data={{
                  labels: ['Activ', 'Inactiv'],
                  datasets: [{
                    data: [stats.coaches.active, stats.coaches.inactive],
                    backgroundColor: ['#339af0', '#e9ecef'],
                    borderWidth: 1
                  }],
                }}
                options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }}
              />
            </div>

            <div className="chart-card">
              <h4>Total membri (comparativ)</h4>
              <Bar
                data={{
                  labels: ['Sportivi', 'Arbitri', 'Antrenori'],
                  datasets: [
                    {
                      label: 'Activi',
                      data: [stats.athletes.active, stats.referees.active, stats.coaches.active],
                      backgroundColor: ['#845ef7', '#51cf66', '#339af0'],
                    },
                    {
                      label: 'Inactivi',
                      data: [stats.athletes.inactive, stats.referees.inactive, stats.coaches.inactive],
                      backgroundColor: ['#e9ecef', '#e9ecef', '#e9ecef'],
                    },
                  ],
                }}
                options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }}
              />
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
